import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BadgeCheck, Building2, Home as HomeIcon, Landmark, MapPin, Search, ShieldCheck, Store, TrendingUp, Warehouse } from "lucide-react";
import { LOGO_ROUND } from "../components/Header";

const TYPES = [
  { icon: HomeIcon, label: "Apartments & Flats", q: "Apartment" },
  { icon: Building2, label: "Houses & Villas", q: "Villa" },
  { icon: Store, label: "Shops & Offices", q: "Office Space" },
  { icon: Warehouse, label: "Warehouses", q: "Warehouse" },
  { icon: Landmark, label: "Plots & Land", q: "Residential Plot" },
];

const WHY = [
  { icon: Search, title: "Easy Search", text: "City, locality, PIN code, budget and BHK filters." },
  { icon: ShieldCheck, title: "Verified Listings", text: "Listings pass platform review & verification." },
  { icon: MapPin, title: "Pan-India Coverage", text: "Metro to Tier-2 cities across India." },
  { icon: TrendingUp, title: "Market Watch", text: "Track price & rental trends by locality." },
];

export default function Home() {
  const [hero, setHero] = useState([]);
  const [slide, setSlide] = useState(0);
  const [locations, setLocations] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [mode, setMode] = useState("rent");
  const [city, setCity] = useState("");
  const [pin, setPin] = useState("");
  const [bhk, setBhk] = useState("");
  const [budget, setBudget] = useState("");
  const [wsType, setWsType] = useState("");
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    api.get("/hero-images").then((r) => setHero(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/properties?featured=true").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
  }, []);

  useEffect(() => {
    if (hero.length < 2) return;
    timer.current = setInterval(() => setSlide((s) => (s + 1) % hero.length), 4500);
    return () => clearInterval(timer.current);
  }, [hero.length]);

  const doSearch = () => {
    const params = new URLSearchParams({ mode });
    if (city) params.set("city", city);
    if (pin) params.set("pin_code", pin);
    if (bhk) params.set("bhk", bhk);
    if (budget) params.set("max_price", budget);
    if (wsType) params.set("property_type", wsType);
    navigate(`/properties?${params.toString()}`);
  };

  const active = hero[slide];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="relative h-[620px] sm:h-[680px]">
          {hero.map((h, i) => (
            <img key={h.image_id} src={h.url} alt={h.title}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${i === slide ? "opacity-100" : "opacity-0"}`} />
          ))}
          <div className="absolute inset-0 bg-slate-950/55" />
          <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-center px-4 sm:px-6 lg:px-8">
            <div className="animate-fade-up mb-4 flex">
              <img src={LOGO_ROUND} alt="Rent-ya-Buy.com — Rent It. Buy It. Own It."
                data-testid="hero-logo"
                className="h-32 w-32 rounded-full border-4 border-white/80 bg-white object-cover shadow-2xl sm:h-44 sm:w-44" />
            </div>
            <p className="eyebrow !text-emerald-400 animate-fade-up">Pan-India Property Marketplace</p>
            <h1 data-testid="hero-title" className="font-heading mt-3 max-w-2xl text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl animate-fade-up">
              {active?.title || "Search. Rent. Buy. Sell."}
            </h1>
            <p className="mt-3 max-w-xl text-base text-slate-200 sm:text-lg animate-fade-up">
              {active?.subtitle || "Your Property. Your Choice."} Homes, flats, villas, plots, offices and shops across India.
            </p>

            {/* SEARCH GLASS PANEL */}
            <div className="mt-8 max-w-3xl rounded-2xl border border-white/20 bg-white/10 p-4 shadow-2xl backdrop-blur-xl sm:p-5" data-testid="hero-search-panel">
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="grid w-full grid-cols-3 bg-white/20">
                  <TabsTrigger value="rent" data-testid="mode-tab-rent" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">RENT</TabsTrigger>
                  <TabsTrigger value="buy" data-testid="mode-tab-buy" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">BUY</TabsTrigger>
                  <TabsTrigger value="sale" data-testid="mode-tab-sale" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">SALE</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className={`mt-4 grid grid-cols-2 gap-3 ${mode === "rent" ? "md:grid-cols-6" : "md:grid-cols-5"}`}>
                {mode === "rent" && (
                  <Select value={wsType} onValueChange={setWsType}>
                    <SelectTrigger data-testid="hero-workspace-select" className="col-span-2 bg-white md:col-span-1"><SelectValue placeholder="Workspace" /></SelectTrigger>
                    <SelectContent>
                      {[["Office Space", "Office"], ["Co-working Space", "Co-working"], ["Virtual Office", "Virtual Office"], ["Desks", "Desks / Seats"]].map(([v, l]) => (
                        <SelectItem key={v} value={v} data-testid={`workspace-option-${v.replace(/\s/g, "-")}`}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger data-testid="hero-city-select-dropdown" className="col-span-2 bg-white md:col-span-1"><SelectValue placeholder="City" /></SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.city} value={l.city} data-testid={`city-option-${l.city}`}>{l.city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={pin} onValueChange={setPin} disabled={!city}>
                  <SelectTrigger data-testid="hero-pin-select-dropdown" className="bg-white"><SelectValue placeholder="Locality / PIN" /></SelectTrigger>
                  <SelectContent>
                    {(locations.find((l) => l.city === city)?.pincodes || []).map((p) => (
                      <SelectItem key={p.pin} value={p.pin} data-testid={`pin-option-${p.pin}`}>{p.locality} · {p.pin}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={bhk} onValueChange={setBhk}>
                  <SelectTrigger data-testid="hero-bhk-select" className="bg-white"><SelectValue placeholder="BHK" /></SelectTrigger>
                  <SelectContent>
                    {["1 BHK", "2 BHK", "3 BHK", "4 BHK"].map((b) => (
                      <SelectItem key={b} value={b} data-testid={`bhk-option-${b}`}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={budget} onValueChange={setBudget}>
                  <SelectTrigger data-testid="hero-budget-select" className="bg-white"><SelectValue placeholder="Max Budget" /></SelectTrigger>
                  <SelectContent>
                    {(mode === "rent"
                      ? [["10000", "₹10k/mo"], ["25000", "₹25k/mo"], ["50000", "₹50k/mo"], ["100000", "₹1L/mo"]]
                      : [["2500000", "₹25 L"], ["5000000", "₹50 L"], ["10000000", "₹1 Cr"], ["25000000", "₹2.5 Cr"]]
                    ).map(([v, l]) => (
                      <SelectItem key={v} value={v} data-testid={`budget-option-${v}`}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button data-testid="hero-search-button" onClick={doSearch} className="col-span-2 gap-2 bg-emerald-600 text-white hover:bg-emerald-700 md:col-span-1">
                  <Search className="h-4 w-4" /> Search
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-medium text-white">
                <span className="flex items-center gap-1 rounded-full bg-white/15 px-3 py-1"><BadgeCheck className="h-3 w-3" /> Verified Owners</span>
                <span className="rounded-full bg-white/15 px-3 py-1">₹0 Brokerage Options</span>
                <span className="rounded-full bg-white/15 px-3 py-1">Instant WhatsApp Connect</span>
                <span className="rounded-full bg-violet-500/80 px-3 py-1">₹99 Owner Direct Contact Pass</span>
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              {hero.map((_, i) => (
                <button key={i} data-testid={`hero-dot-${i}`} onClick={() => setSlide(i)}
                  className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-white" : "w-2 bg-white/40"}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROPERTY TYPES */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <p className="eyebrow">Browse by type</p>
        <h2 className="font-heading mt-2 text-2xl font-bold tracking-tight sm:text-3xl">Every property, one platform</h2>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {TYPES.map((t) => (
            <button key={t.label} data-testid={`type-${t.q.replace(/\s/g, "-")}`}
              onClick={() => navigate(`/properties?property_type=${encodeURIComponent(t.q)}`)}
              className="group flex flex-col items-center gap-3 rounded-2xl border bg-card p-6 text-center transition-all hover:-translate-y-1 hover:border-blue-300 hover:shadow-lg">
              <t.icon className="h-8 w-8 text-blue-600 transition-transform group-hover:scale-110" />
              <span className="text-sm font-semibold">{t.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-slate-100/70 py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between">
            <div>
              <p className="eyebrow">Handpicked</p>
              <h2 className="font-heading mt-2 text-2xl font-bold tracking-tight sm:text-3xl">Featured Properties</h2>
            </div>
            <Button data-testid="view-all-properties-button" variant="outline" onClick={() => navigate("/properties")}>View All</Button>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-properties-grid">
            {featured.map((p) => <PropertyCard key={p.property_id} property={p} />)}
          </div>
        </div>
      </section>

      {/* WHY + HOW */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <p className="eyebrow">Why Rent-ya-Buy.com</p>
        <h2 className="font-heading mt-2 text-2xl font-bold tracking-tight sm:text-3xl">Built for trust & speed</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <div key={w.title} className="rounded-2xl border bg-card p-6 animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <w.icon className="h-8 w-8 text-emerald-600" />
              <h3 className="font-heading mt-4 text-lg font-semibold">{w.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{w.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <div className="rounded-2xl bg-slate-900 p-8 text-white">
            <h3 className="font-heading text-xl font-bold">For Property Seekers</h3>
            <ol className="mt-4 space-y-2 text-sm text-slate-300">
              {["Register free", "Search by city / locality / PIN", "Filter type, budget, BHK", "Shortlist to Favorites / Cart", "Unlock owner contact (₹99 pass)", "Verify & proceed directly"].map((s, i) => (
                <li key={s} className="flex gap-3"><span className="font-price font-bold text-emerald-400">{String(i + 1).padStart(2, "0")}</span>{s}</li>
              ))}
            </ol>
          </div>
          <div className="rounded-2xl border bg-card p-8">
            <h3 className="font-heading text-xl font-bold">For Property Owners</h3>
            <ol className="mt-4 space-y-2 text-sm text-muted-foreground">
              {["Register as Owner / Agent / Builder", "Complete verification (mock OTP)", "Post property with up to 6 photos", "Select Rent / Sale", "Admin review & approval", "Receive enquiries & WhatsApp leads"].map((s, i) => (
                <li key={s} className="flex gap-3"><span className="font-price font-bold text-blue-600">{String(i + 1).padStart(2, "0")}</span>{s}</li>
              ))}
            </ol>
            <Button data-testid="home-post-property-cta" className="mt-6 bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => navigate("/post-property")}>
              Post Your Property — Free
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
